import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from '../config/axios';
import { useAuth } from '../contexts/AuthContext';

const InviteIconContainer = styled(Box)(({ theme, showTooltip }) => ({
  position: 'fixed',
  bottom: '340px', // Positioned above DigitaleSprechstundeIcon
  right: '20px',
  zIndex: showTooltip ? 9990 : 9998, // Lower z-index when tooltip is shown
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const InviteIconOverlay = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #ffeb3b 0%, #fbc02d 100%)', // Yellow gradient
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(255, 235, 59, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(255, 235, 59, 0.5)',
  },
}));

const InviteTooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '10px',
  right: '80px',
  width: '280px',
  background: 'linear-gradient(135deg, #fbc02d 0%, #f9a825 100%)', // Yellow gradient
  border: '2px solid #ffeb3b', // Yellow border
  borderRadius: '12px',
  padding: theme.spacing(2),
  zIndex: 10010,
  boxShadow: '0 12px 40px rgba(255, 235, 59, 0.4)',
  backdropFilter: 'blur(10px)',
  animation: 'tooltipSlideIn 0.3s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: '20px',
    right: '-8px',
    width: 0,
    height: 0,
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderLeft: '8px solid #fbc02d', // Yellow arrow pointing right
  },
  '@keyframes tooltipSlideIn': {
    '0%': { opacity: 0, transform: 'translateY(10px) scale(0.95)' },
    '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
  },
}));

const InviteIcon = () => {
  const { user } = useAuth();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showHoverTooltip, setShowHoverTooltip] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showSocialDialog, setShowSocialDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleIconClick = () => setShowTooltip(!showTooltip);
  
  const handleEmailInvite = () => {
    setShowEmailDialog(true);
    setShowTooltip(false);
  };

  const handleSocialShare = () => {
    setShowSocialDialog(true);
    setShowTooltip(false);
  };

  const handleCloseDialogs = () => {
    setShowEmailDialog(false);
    setShowSocialDialog(false);
    setEmail('');
    setMessage('');
  };

  const sendEmailInvite = async () => {
    try {
      const response = await axios.post('/invite/email', {
        email,
        message: message || 'Schau dir das Cyberlagebild an - eine tolle Plattform für Cybersicherheit!'
      });
      
      setSnackbar({
        open: true,
        message: 'Einladung erfolgreich gesendet!',
        severity: 'success'
      });
      handleCloseDialogs();
    } catch (error) {
      let errorMessage = 'Fehler beim Senden der Einladung. Bitte versuchen Sie es erneut.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Bitte loggen Sie sich ein, um Einladungen zu senden.';
      } else if (error.response?.status === 422) {
        // Check if it's a validation error with specific details
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (Array.isArray(detail)) {
            // Pydantic validation errors come as an array
            const firstError = detail[0];
            if (firstError?.loc && firstError.loc.includes('message')) {
              // Check the specific error message
              if (firstError.msg && firstError.msg.includes('malicious content')) {
                errorMessage = 'Die Nachricht enthält nicht erlaubte Inhalte (z.B. HTML-Tags oder Skripte). Bitte verwenden Sie nur normalen Text.';
              } else if (firstError.msg && firstError.msg.includes('500 characters')) {
                errorMessage = 'Die Nachricht ist zu lang. Bitte verwenden Sie maximal 500 Zeichen.';
              } else {
                errorMessage = 'Die Nachricht enthält ungültige Inhalte.';
              }
            } else if (firstError?.loc && firstError.loc.includes('email')) {
              errorMessage = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
            } else {
              errorMessage = 'Die eingegebenen Daten sind ungültig.';
            }
          } else {
            errorMessage = detail;
          }
        } else {
          errorMessage = 'Die eingegebenen Daten sind ungültig.';
        }
      } else if (error.response?.status === 429) {
        // Rate limit exceeded
        errorMessage = error.response.data.detail || 'Sie haben das Limit für E-Mail-Einladungen erreicht. Bitte versuchen Sie es später erneut.';
      } else if (error.response?.status === 409) {
        // Duplicate recipient
        errorMessage = error.response.data.detail || 'Sie haben bereits eine Einladung an diese E-Mail-Adresse gesendet. Bitte wählen Sie eine andere Adresse.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const shareToSocialMedia = async () => {
    try {
      const shareText = message || 'Schau dir das Cyberlagebild an - eine tolle Plattform für Cybersicherheit!';
      const shareUrl = 'https://cyberlagebild.de';
      let shareType = '';
      
      // First, validate the message by trying to log it (this will catch validation errors)
      try {
        await axios.post('/invite/share', {
          share_type: 'validation_check',
          details: `Shared: ${shareText}`
        });
      } catch (validationError) {
        // Handle validation errors specifically
        if (validationError.response?.status === 422) {
          let errorMessage = 'Die Nachricht enthält ungültige Inhalte.';
          
          if (validationError.response?.data?.detail) {
            const detail = validationError.response.data.detail;
            if (Array.isArray(detail)) {
              const firstError = detail[0];
              if (firstError?.loc && firstError.loc.includes('details')) {
                if (firstError.msg && firstError.msg.includes('malicious content')) {
                  errorMessage = 'Die Nachricht enthält nicht erlaubte Inhalte (z.B. HTML-Tags oder Skripte). Bitte verwenden Sie nur normalen Text.';
                } else if (firstError.msg && firstError.msg.includes('500 characters')) {
                  errorMessage = 'Die Nachricht ist zu lang. Bitte verwenden Sie maximal 500 Zeichen.';
                }
              }
            }
          }
          
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error'
          });
          return; // Don't proceed with sharing
        }
        // If it's not a validation error, continue with sharing (logging might fail but sharing should work)
      }
      
      // Check if Web Share API is supported
      if (navigator.share) {
        await navigator.share({
          title: 'Cyberlagebild - Cybersicherheit verstehen',
          text: shareText,
          url: shareUrl
        });
        
        shareType = 'web_share';
      } else {
        // Fallback: Copy to clipboard
        const shareContent = `${shareText} ${shareUrl}`;
        await navigator.clipboard.writeText(shareContent);
        
        shareType = 'clipboard_copy';
        setSnackbar({
          open: true,
          message: 'Link in die Zwischenablage kopiert!',
          severity: 'success'
        });
      }
      
      // Log the share activity (now that we know the content is valid)
      try {
        await axios.post('/invite/share', {
          share_type: shareType,
          details: `Shared: ${shareText}`
        });
      } catch (logError) {
        console.warn('Failed to log share activity:', logError);
        // Don't show error to user as this is just logging
      }
      
      handleCloseDialogs();
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled the share dialog
        return;
      }
      
      setSnackbar({
        open: true,
        message: 'Fehler beim Teilen. Bitte versuchen Sie es erneut.',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <InviteIconContainer showTooltip={showTooltip || showHoverTooltip}>
      <InviteIconOverlay 
        onClick={handleIconClick} 
        onMouseEnter={() => setShowHoverTooltip(true)}
        onMouseLeave={() => setShowHoverTooltip(false)}
        title="Freunde einladen"
      >
        <Typography
          sx={{
            fontSize: '28px',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
          }}
        >
          📧
        </Typography>
      </InviteIconOverlay>
      
      {showHoverTooltip && (
        <InviteTooltip>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
            Freunde einladen
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Teile das Cyberlagebild mit anderen
          </Typography>
        </InviteTooltip>
      )}
      
      {showTooltip && (
        <InviteTooltip>
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 'bold', marginBottom: 1, textAlign: 'center' }}>
            Freunde einladen – Aus Austausch wird Vorsprung.
          </Typography>
          <Typography variant="body2" sx={{ color: '#fff3e0', marginBottom: 2, textAlign: 'center', fontStyle: 'italic' }}>
            Teile das Cyberlagebild mit anderen!
          </Typography>
          {!user && (
            <Typography variant="body2" sx={{ color: '#ffcc80', marginBottom: 1, textAlign: 'center', fontSize: '0.7rem' }}>
              (E-Mail-Einladungen erfordern eine Anmeldung)
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: '#ffe0b2', marginBottom: 2, textAlign: 'center', fontSize: '0.85rem' }}>
            Entweder direkt hier per E-Mail, oder über ein installiertes Programm auf deinem Gerät.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              size="small" 
              onClick={handleEmailInvite} 
              disabled={!user}
              sx={{ 
                background: user ? 'linear-gradient(135deg, #fff176 0%, #ffeb3b 100%)' : 'rgba(255, 255, 255, 0.3)', 
                color: '#ffffff', 
                fontWeight: 'bold', 
                padding: '6px 12px', 
                borderRadius: '6px', 
                fontSize: '0.75rem',
                '&:hover': user ? { 
                  background: 'linear-gradient(135deg, #ffeb3b 0%, #fbc02d 100%)', 
                  transform: 'translateY(-1px)' 
                } : {}, 
                transition: 'all 0.2s ease-in-out' 
              }}
              title={!user ? 'Bitte loggen Sie sich ein, um E-Mail-Einladungen zu senden' : 'E-Mail-Einladung senden'}
            >
              E-Mail
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleSocialShare} 
              sx={{ 
                color: '#ffffff', 
                borderColor: '#ffffff', 
                padding: '6px 12px', 
                borderRadius: '6px', 
                fontSize: '0.75rem',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                  borderColor: '#fff3e0' 
                } 
              }}
            >
              Teilen
            </Button>
          </Box>
        </InviteTooltip>
      )}

      {/* Email Invitation Dialog */}
      <Dialog open={showEmailDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #fbc02d 0%, #f9a825 100%)', color: '#ffffff' }}>
          E-Mail Einladung senden
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="E-Mail Adresse"
            type="email"
            fullWidth
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            margin="dense"
            label="Persönliche Nachricht (optional)."
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Schau dir das Cyberlagebild an - eine tolle Plattform für Cybersicherheit!"
            helperText="Max. 500 Zeichen, nur Text erlaubt (keine HTML-Tags)"
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            Abbrechen
          </Button>
          <Button 
            onClick={sendEmailInvite} 
            variant="contained"
            sx={{ 
              background: 'linear-gradient(135deg, #ffeb3b 0%, #fbc02d 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #fbc02d 0%, #f9a825 100%)' }
            }}
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Social Media Sharing Dialog */}
      <Dialog open={showSocialDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #fbc02d 0%, #f9a825 100%)', color: '#ffffff' }}>
          Link zu cyberlagebild.de teilen
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            margin="dense"
            label="Schreibe eine Nachricht (optional)."
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Schau dir das Cyberlagebild an - eine tolle Plattform für Cybersicherheit!"
            helperText="Max. 500 Zeichen, nur Text erlaubt (keine HTML-Tags)"
            inputProps={{ maxLength: 500 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
            <Button 
              variant="contained"
              onClick={shareToSocialMedia}
              sx={{ 
                background: 'linear-gradient(135deg, #ffeb3b 0%, #fbc02d 100%)',
                color: '#ffffff',
                fontWeight: 'bold',
                padding: '12px 24px',
                borderRadius: '8px',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #fbc02d 0%, #f9a825 100%)',
                  transform: 'translateY(-1px)' 
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Teilen
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </InviteIconContainer>
  );
};

export default InviteIcon;
